import { useEffect, useState } from "react";
import { toast } from "sonner";
import { saveVzGoal } from "@/lib/verizonSalesApi";

export const GOAL_COLS = [
  { card: "acts", goal: "acts", label: "Activations" }, { card: "upgrades", goal: "upgrades", label: "Upgrades" },
  { card: "edge", goal: "edge", label: "Edge" }, { card: "byod", goal: "byod", label: "BYOD" },
  { card: "tablets", goal: "tablets", label: "Tablets" }, { card: "fwa", goal: "fwa", label: "FWA" },
  { card: "twp", goal: "twp_plus", label: "TWP+" }, { card: "autopay", goal: "autopay", label: "AutoPay" },
  { card: "topups", goal: "topups", label: "Top-Ups" },
  ...[2, 3, 4, 5, 6, 7].map((n) => ({ card: `mr${n}`, goal: `mr${n}`, label: `${n}MR%` })),
  { card: "rewards", goal: "rewards", label: "Rewards" }, { card: "zulu", goal: "zulu", label: "Zulu" },
  { card: "lifeline", goal: "lifeline", label: "Lifeline" }, { card: "csat", goal: "csat", label: "CSAT" },
];
const TOP = ["acts", "upgrades"];

// The goal shown for a store: dealer's raised goal if set, otherwise Prime Media's goal (admin minimum or quota).
export const goalOf = (col, g, quota, mode) => {
  if (TOP.includes(col.goal)) {
    const v = mode === "admin" ? g?.[`min_goal_${col.goal}`] : g?.[`goal_${col.goal}`];
    return v ?? quota;
  }
  return mode === "admin" ? quota : (g?.goals?.[col.goal] ?? quota);
};

const toInput = (v, rate) => (v == null ? "" : rate ? String(Math.round(v * 1000) / 10) : String(Math.round(v * 100) / 100));

export const GoalCell = ({ col, card, g, mode, editable, month, onSaved }) => {
  const rate = card?.kind === "rate";
  const quota = card?.quota;
  const goal = goalOf(col, g, quota, mode);
  const canEdit = editable && (mode === "dealer" || TOP.includes(col.goal));
  const [val, setVal] = useState(toInput(goal, rate));
  useEffect(() => setVal(toInput(goal, rate)), [goal, rate]);
  const fmt = card?.fmt || ((v) => v ?? "—");
  const track = card?.track ?? card?.cur;
  const dot = !goal || (rate && card?.cur == null) ? "bg-slate-300" : (track || 0) >= goal ? "bg-emerald-500" : "bg-red-500";
  const tid = `vz-goal-${col.goal}-${g?.door_tsp}`;

  const save = async () => {
    if (val === toInput(goal, rate)) return;
    const n = val === "" ? null : Number(val);
    const body = { door_tsp: g.door_tsp, month };
    if (TOP.includes(col.goal)) body[`goal_${col.goal}`] = n;
    else body.goals = { [col.goal]: n };
    try { await saveVzGoal(mode, body); toast.success(`${col.label} goal saved for ${g.store_id}`); onSaved(); }
    catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : "Save failed"); setVal(toInput(goal, rate)); }
  };
  const raised = mode === "admin" && !TOP.includes(col.goal) && g?.goals?.[col.goal] != null;
  return (
    <td className="px-1.5 py-1.5 text-center border-l border-slate-100 align-top whitespace-nowrap" data-testid={tid}>
      {canEdit ? (
        <div className="relative inline-block">
          <input type="number" min="0" step="any" value={val} onChange={(e) => setVal(e.target.value)} onBlur={save}
            onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} placeholder="—"
            className="h-7 w-16 rounded border border-slate-200 px-1.5 pr-4 text-xs text-center tabular-nums focus:border-teal focus:outline-none" data-testid={`${tid}-input`} />
          {rate && <span className="absolute right-1 top-1.5 text-[10px] text-slate-400">%</span>}
        </div>
      ) : <b className="text-navy tabular-nums" data-testid={`${tid}-value`}>{fmt(goal)}</b>}
      {raised && <div className="text-[10px] text-indigo-500">dealer {fmt(g.goals[col.goal])}</div>}
      <div className="flex items-center justify-center gap-1 text-[11px] text-slate-500 mt-0.5" data-testid={`${tid}-current`}>
        <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />{fmt(card?.cur)}
      </div>
    </td>
  );
};
